import React from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  LinearProgress,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useOnboarding } from "../context/OnboardingContext";

// Custom step connector with gradient
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  "& .MuiStepConnector-line": {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.grey[300],
    borderRadius: 1,
  },
  "&.Mui-active .MuiStepConnector-line": {
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  },
  "&.Mui-completed .MuiStepConnector-line": {
    background: "linear-gradient(90deg, #10B981 0%, #059669 100%)",
  },
}));

// Custom step icon
const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.grey[300],
  zIndex: 1,
  color: theme.palette.grey[600],
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "1.2rem",
  fontWeight: "bold",
  ...(ownerState.active && {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
  }),
  ...(ownerState.completed && {
    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    color: "white",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, step } = props;
  const { ONBOARDING_STEPS } = useOnboarding();

  const stepData = Object.values(ONBOARDING_STEPS).find(
    (s) => s.order === step + 1
  );
  const icon = stepData?.icon || "?";

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {completed ? "✓" : icon}
    </ColorlibStepIconRoot>
  );
}

const ProgressStepper = ({ variant = "horizontal" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    getAllSteps,
    currentStep,
    getProgressPercentage,
    getCurrentStepObject,
  } = useOnboarding();

  const steps = getAllSteps();
  const currentStepObject = getCurrentStepObject();
  const progressPercentage = getProgressPercentage();

  // Debug logging
  // console.log("ProgressStepper Debug:", {
  //   steps: steps.length,
  //   currentStep,
  //   currentStepObject,
  //   progressPercentage,
  // });

  // Fallback if no data
  if (!steps || steps.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Loading progress...
        </Typography>
      </Box>
    );
  }

  if (variant === "compact") {
    return (
      <Box sx={{ width: "100%", mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {currentStepObject?.title}
          </Typography>
          <Chip
            label={`${progressPercentage}% Complete`}
            color="primary"
            size="small"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontWeight: 600,
            }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.palette.grey[200],
            "& .MuiLinearProgress-bar": {
              background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 4,
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {currentStepObject?.description}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      {/* Progress Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}
          >
            {currentStepObject?.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentStepObject?.description}
          </Typography>
        </Box>
        <Chip
          label={`${progressPercentage}% Complete`}
          color="primary"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "0.875rem",
          }}
        />
      </Box>

      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.grey[200],
          mb: 3,
          "& .MuiLinearProgress-bar": {
            background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 4,
          },
        }}
      />

      {/* Stepper */}
      <Stepper
        alternativeLabel={!isMobile}
        activeStep={steps.findIndex((step) => step.id === currentStep)}
        connector={<ColorlibConnector />}
        sx={{
          "& .MuiStepLabel-root": {
            "& .MuiStepLabel-label": {
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              fontWeight: 500,
            },
            "& .MuiStepLabel-label.Mui-active": {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
            "& .MuiStepLabel-label.Mui-completed": {
              color: theme.palette.success.main,
              fontWeight: 600,
            },
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.id}>
            <StepLabel
              StepIconComponent={ColorlibStepIcon}
              StepIconProps={{ step: index }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {step.title}
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                )}
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default ProgressStepper;
